import { useState, useRef, useCallback } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { Product, upsertProduct, deleteProduct } from '@/lib/posStorage';
import { getCurrencySymbol } from '@/lib/bch';
import { FREE_PRODUCT_LIMIT } from '@/lib/pro';
import { cn } from '@/lib/utils';

// ─── Image helpers ────────────────────────────────────────────────────────────

function resizeImageDataUrl(file: File, maxPx = 400): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.onerror = reject;
      img.src = e.target!.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Product form modal ────────────────────────────────────────────────────────

interface ProductFormProps {
  initial?: Product;
  currency: string;
  isPro: boolean;
  taxRates: import('@/lib/posStorage').TaxRate[];
  onSave: (p: Product) => void;
  onCancel: () => void;
  onUpgrade: () => void;
}

function ProductForm({ initial, currency, isPro, taxRates, onSave, onCancel, onUpgrade }: ProductFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [price, setPrice] = useState(initial?.price?.toString() ?? '');
  const [category, setCategory] = useState(initial?.category ?? 'General');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [imageDataUrl, setImageDataUrl] = useState<string | undefined>(initial?.imageDataUrl);
  const [available, setAvailable] = useState(initial?.available ?? true);
  const [taxRateId, setTaxRateId] = useState<string | undefined>(initial?.taxRateId);
  const [imgLoading, setImgLoading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const currSymbol = getCurrencySymbol(currency);

  const handleImagePick = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgLoading(true);
    try {
      const dataUrl = await resizeImageDataUrl(file, 400);
      setImageDataUrl(dataUrl);
    } catch {
      alert('Could not load image. Please try another file.');
    } finally {
      setImgLoading(false);
      e.target.value = '';
    }
  }, []);

  const handleSave = () => {
    const parsedPrice = parseFloat(price);
    if (!name.trim()) return alert('Please enter a product name.');
    if (isNaN(parsedPrice) || parsedPrice < 0) return alert('Please enter a valid price.');
    const product: Product = {
      id: initial?.id ?? crypto.randomUUID(),
      name: name.trim(),
      price: parsedPrice,
      category: isPro ? (category.trim() || 'General') : 'General',
      description: description.trim() || undefined,
      imageDataUrl: isPro ? imageDataUrl : undefined,
      available,
      taxRateId: isPro ? taxRateId : undefined,
    };
    onSave(product);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white dark:bg-zinc-900 rounded-t-3xl p-5 pb-safe overflow-y-auto max-h-[92dvh]">
        <div className="w-10 h-1 bg-zinc-200 dark:bg-zinc-700 rounded-full mx-auto mb-5" />
        <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50 mb-5">
          {initial ? 'Edit Product' : 'Add Product'}
        </h2>

        {/* Image picker — Pro only */}
        {isPro ? (
          <div className="mb-5 flex items-center gap-4">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="relative w-20 h-20 rounded-2xl bg-zinc-100 dark:bg-zinc-800 border-2 border-dashed border-zinc-300 dark:border-zinc-600 flex items-center justify-center overflow-hidden shrink-0 transition hover:border-[#0AC18E]"
            >
              {imgLoading ? (
                <span className="text-xs text-zinc-400 animate-pulse">...</span>
              ) : imageDataUrl ? (
                <img src={imageDataUrl} alt="product" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl">📷</span>
              )}
            </button>
            <div className="text-sm text-zinc-500 dark:text-zinc-400">
              <p className="font-medium text-zinc-700 dark:text-zinc-200 mb-0.5">Product photo</p>
              <p>Tap to upload from your device.</p>
              {imageDataUrl && (
                <button
                  type="button"
                  onClick={() => setImageDataUrl(undefined)}
                  className="text-red-400 text-xs mt-1 hover:underline"
                >
                  Remove photo
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} />
          </div>
        ) : (
          /* Locked photo section for free users */
          <button
            type="button"
            onClick={onUpgrade}
            className="mb-5 w-full flex items-center gap-3 bg-[#0AC18E]/5 dark:bg-[#0AC18E]/10 border border-[#0AC18E]/20 dark:border-[#0AC18E]/30 rounded-2xl p-4 text-left"
          >
            <div className="w-12 h-12 rounded-xl bg-[#0AC18E]/10 dark:bg-[#0AC18E]/20 flex items-center justify-center shrink-0 text-2xl">
              📷
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">Product photos</p>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">Upgrade to Pro to add images</p>
            </div>
            <span className="text-[#0AC18E] font-bold text-sm">Pro →</span>
          </button>
        )}

        {/* Name */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
            Product Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Iced Coffee"
            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
          />
        </div>

        {/* Price */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
            Price ({currSymbol}) *
          </label>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0.00"
            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
          />
        </div>

        {/* Category — Pro only */}
        {isPro ? (
          <div className="mb-4">
            <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
              Category
            </label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. Drinks, Food, Merch"
              className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={onUpgrade}
            className="mb-4 w-full flex items-center justify-between bg-[#0AC18E]/5 dark:bg-[#0AC18E]/10 border border-[#0AC18E]/20 dark:border-[#0AC18E]/30 rounded-xl px-4 py-3"
          >
            <span className="text-sm text-zinc-700 dark:text-zinc-200 font-medium">
              🗂️ Categories <span className="font-normal opacity-60">— Pro feature</span>
            </span>
            <span className="text-xs font-bold text-[#0AC18E] bg-[#0AC18E]/10 dark:bg-[#0AC18E]/20 px-2 py-0.5 rounded-full">
              Upgrade →
            </span>
          </button>
        )}

        {/* Description */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
            Description (optional)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description..."
            rows={2}
            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E] resize-none"
          />
        </div>

        {/* Tax rate picker — Pro only, only if tax rates exist */}
        {isPro && taxRates.length > 0 && (
          <div className="mb-4">
            <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
              Tax Rate
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setTaxRateId(undefined)}
                className={cn('px-3 py-2 rounded-xl text-sm font-medium border transition-all',
                  taxRateId === undefined
                    ? 'bg-[#0AC18E] border-[#0AC18E] text-white'
                    : 'bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300'
                )}
              >
                Default
              </button>
              {taxRates.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setTaxRateId(r.id)}
                  className={cn('px-3 py-2 rounded-xl text-sm font-medium border transition-all',
                    taxRateId === r.id
                      ? 'bg-[#0AC18E] border-[#0AC18E] text-white'
                      : 'bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300'
                  )}
                >
                  {r.name} ({r.rate}%)
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Available toggle */}
        <div className="mb-6 flex items-center justify-between bg-zinc-50 dark:bg-zinc-800 rounded-xl px-4 py-3 border border-zinc-200 dark:border-zinc-700">
          <div>
            <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">Available for sale</p>
            <p className="text-xs text-zinc-400">Hide this product from the menu</p>
          </div>
          <button
            type="button"
            onClick={() => setAvailable((v) => !v)}
            className={cn(
              'relative w-12 h-6 rounded-full transition-colors duration-200 shrink-0',
              available ? 'bg-[#0AC18E]' : 'bg-zinc-300 dark:bg-zinc-600'
            )}
          >
            <span className={cn(
              'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200',
              available ? 'translate-x-6' : 'translate-x-0'
            )} />
          </button>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={onCancel}
            className="flex-1 py-4 rounded-2xl text-base font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700"
          >
            Cancel
          </button>
          <button type="button" onClick={handleSave}
            className="flex-[2] py-4 rounded-2xl text-base font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98 transition-all"
          >
            {initial ? 'Save Changes' : 'Add Product'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Product card ─────────────────────────────────────────────────────────────

function ProductCardManage({ product, currency, onEdit, onDelete }: {
  product: Product; currency: string; onEdit: () => void; onDelete: () => void;
}) {
  const currSymbol = getCurrencySymbol(currency);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className={cn(
      'flex items-center gap-3 bg-white dark:bg-zinc-800 rounded-2xl p-3 border transition-opacity',
      product.available ? 'border-zinc-100 dark:border-zinc-700' : 'border-zinc-200 dark:border-zinc-700 opacity-60'
    )}>
      <div className="w-14 h-14 rounded-xl bg-zinc-100 dark:bg-zinc-700 shrink-0 overflow-hidden flex items-center justify-center">
        {product.imageDataUrl
          ? <img src={product.imageDataUrl} alt={product.name} className="w-full h-full object-cover" />
          : <span className="text-2xl">🛍️</span>
        }
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-zinc-900 dark:text-zinc-50 truncate text-sm">{product.name}</p>
        <p className="text-xs text-zinc-400 truncate">{product.category}</p>
        <p className="text-sm font-bold text-[#0AC18E]">{currSymbol}{product.price.toFixed(2)}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!confirmDelete ? (
          <>
            <button onClick={onEdit}
              className="w-9 h-9 rounded-xl bg-zinc-100 dark:bg-zinc-700 flex items-center justify-center text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-600 transition-colors"
            >✏️</button>
            <button onClick={() => setConfirmDelete(true)}
              className="w-9 h-9 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
            >🗑️</button>
          </>
        ) : (
          <>
            <button onClick={() => setConfirmDelete(false)}
              className="px-3 h-9 rounded-xl bg-zinc-100 dark:bg-zinc-700 text-xs font-medium text-zinc-500"
            >Keep</button>
            <button onClick={onDelete}
              className="px-3 h-9 rounded-xl bg-red-500 text-xs font-bold text-white shadow-sm"
            >Delete</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export function MenuManageView() {
  const { products, refreshProducts, settings, isPro, setView } = usePOS();
  const taxRates = settings.taxRates ?? [];
  const [editing, setEditing] = useState<Product | null | 'new'>(null);
  const [search, setSearch] = useState('');

  const atLimit = !isPro && products.length >= FREE_PRODUCT_LIMIT;

  const categories = Array.from(new Set(products.map((p) => p.category))).sort();
  const filtered = products.filter((p) =>
    search === '' ||
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  );
  const grouped = categories.reduce<Record<string, Product[]>>((acc, cat) => {
    acc[cat] = filtered.filter((p) => p.category === cat);
    return acc;
  }, {});

  const handleSave = (product: Product) => {
    upsertProduct(product);
    refreshProducts();
    setEditing(null);
  };

  const handleDelete = (id: string) => {
    deleteProduct(id);
    refreshProducts();
  };

  const handleAddNew = () => {
    if (atLimit) {
      setView('upgrade');
      return;
    }
    setEditing('new');
  };

  return (
    <div className="flex flex-col gap-4 pb-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">Menu Items</h2>
          <p className="text-sm text-zinc-400">
            {isPro
              ? `${products.length} product${products.length !== 1 ? 's' : ''}`
              : `${products.length} / ${FREE_PRODUCT_LIMIT} free products`}
          </p>
        </div>
        {isPro && (
          <span className="text-xs font-bold text-[#0AC18E] bg-[#0AC18E]/10 dark:bg-[#0AC18E]/20 px-2.5 py-1 rounded-full border border-[#0AC18E]/20">
            ✓ Pro
          </span>
        )}
        <button
          onClick={handleAddNew}
          className={cn(
            'flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold shadow-sm active:scale-95 transition-all',
            atLimit
              ? 'bg-[#0AC18E] text-white shadow-[#0AC18E]/30 hover:bg-[#09AF81]'
              : 'bg-[#0AC18E] text-white shadow-[#0AC18E]/30 hover:bg-[#09AF81]'
          )}
        >
          <span className="text-base leading-none">{atLimit ? '⭐' : '＋'}</span>
          {atLimit ? 'Upgrade' : 'Add Item'}
        </button>
      </div>

      {/* Free tier limit bar */}
      {!isPro && products.length > 0 && (
        <div className="bg-[#0AC18E]/5 dark:bg-[#0AC18E]/10 border border-[#0AC18E]/20 dark:border-[#0AC18E]/30 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">Free plan</p>
            <p className="text-sm font-bold text-[#0AC18E]">
              {products.length} / {FREE_PRODUCT_LIMIT} products
            </p>
          </div>
          {/* Progress bar */}
          <div className="w-full bg-[#0AC18E]/15 dark:bg-[#0AC18E]/20 rounded-full h-2 mb-3">
            <div
              className="bg-[#0AC18E] h-2 rounded-full transition-all"
              style={{ width: `${Math.min(100, (products.length / FREE_PRODUCT_LIMIT) * 100)}%` }}
            />
          </div>
          <button
            onClick={() => setView('upgrade')}
            className="text-xs font-bold text-[#0AC18E] hover:underline"
          >
            Upgrade to Pro for unlimited products →
          </button>
        </div>
      )}

      {/* Search */}
      {products.length > 4 && (
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products..."
          className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
        />
      )}

      {/* Empty state */}
      {products.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-zinc-200 dark:border-zinc-700 rounded-3xl">
          <div className="text-5xl mb-4">🛍️</div>
          <p className="text-zinc-700 dark:text-zinc-200 font-semibold text-base mb-1">No products yet</p>
          <p className="text-sm text-zinc-400 dark:text-zinc-500 max-w-xs mb-5">
            Add up to {FREE_PRODUCT_LIMIT} products free. Upgrade for unlimited.
          </p>
          <button
            onClick={() => setEditing('new')}
            className="px-6 py-3 rounded-xl bg-[#0AC18E] text-white font-bold text-sm shadow-sm hover:bg-[#09AF81] active:scale-95 transition-all"
          >
            Add your first product
          </button>
        </div>
      )}

      {/* Product list */}
      {search !== '' ? (
        <div className="space-y-2">
          {filtered.map((p) => (
            <ProductCardManage key={p.id} product={p} currency={settings.currency}
              onEdit={() => setEditing(p)} onDelete={() => handleDelete(p.id)} />
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-sm text-zinc-400 py-8">No products match "{search}"</p>
          )}
        </div>
      ) : (
        <div className="space-y-5">
          {isPro
            ? Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2 px-1">{cat}</p>
                <div className="space-y-2">
                  {items.map((p) => (
                    <ProductCardManage key={p.id} product={p} currency={settings.currency}
                      onEdit={() => setEditing(p)} onDelete={() => handleDelete(p.id)} />
                  ))}
                </div>
              </div>
            ))
            : filtered.map((p) => (
              <ProductCardManage key={p.id} product={p} currency={settings.currency}
                onEdit={() => setEditing(p)} onDelete={() => handleDelete(p.id)} />
            ))
          }
        </div>
      )}

      {/* Product form modal */}
      {editing !== null && (
        <ProductForm
          initial={editing === 'new' ? undefined : editing}
          currency={settings.currency}
          isPro={isPro}
          taxRates={taxRates}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
          onUpgrade={() => { setEditing(null); setView('upgrade'); }}
        />
      )}
    </div>
  );
}
